import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import SuggestionsTab from "./SuggestionsTab";
import ContactPage from "./ContactPage";
import graphic from "./graphic";
/* ---------- auto-load images ---------- */
function importAll(r) {
  return r.keys().map((k) => ({ key: k, url: r(k) }));
}

const imported = importAll(
  require.context("./sneakers", false, /\.(png|jpe?g|webp|gif)$/i)
);

const clothingImported = importAll(
  require.context("./clothing", false, /\.(png|jpe?g|webp|gif)$/i)
);

/* ---------- parse filenames for socials ---------- */
function parseMeta(keyPath, url) {
  const file = keyPath.replace(/^\.\//, "");
  const base = file.replace(/\.(png|jpe?g|webp|gif)$/i, "");
  const parts = base.split("__");

  const rawName = parts[0] || "Untitled";
  const name = decodeURIComponent(rawName).trim();

  let ttid = "";
  let ttuser = "";
  let igid = "";

  for (const token of parts.slice(1)) {
    if (token.startsWith("ttid-")) ttid = token.slice(5);
    else if (token.startsWith("ttuser-")) ttuser = token.slice(7);
    else if (token.startsWith("igid-")) igid = token.slice(5);
    else if (token.startsWith("tt-")) {
      const rest = token.slice(3);
      const lastDash = rest.lastIndexOf("-");
      if (lastDash > 0) {
        ttuser = rest.slice(0, lastDash);
        ttid = rest.slice(lastDash + 1);
      } else if (/^\d+$/.test(rest)) {
        ttid = rest;
      }
    }
  }

  const tiktok = ttid
    ? ttuser
      ? `https://www.tiktok.com/@${ttuser}/video/${ttid}`
      : `https://www.tiktok.com/video/${ttid}`
    : "";

  const instagram = igid ? `https://www.instagram.com/p/${igid}` : "";

  return { name, tiktok, instagram, src: url };
}

const sneakers = imported
  .map((f) => parseMeta(f.key, f.url))
  .sort((a, b) => a.name.localeCompare(b.name));

const clothing = clothingImported
  .map((f) => parseMeta(f.key, f.url))
  .sort((a, b) => a.name.localeCompare(b.name));

function getTabFromHash() {
  const hash = window.location.hash.replace("#", "");
  return ["sneakers", "clothing", "suggest", "contact"].includes(hash)
    ? hash
    : "sneakers";
}

export default function AdamsSneakerGallery() {
  const [activeTab, setActiveTab] = useState(getTabFromHash);
  const [visible, setVisible] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const touchStart = useRef(null);

  const currentItems =
    activeTab === "sneakers"
      ? sneakers
      : activeTab === "clothing"
      ? clothing
      : [];

  const switchTab = (tab) => {
    if (tab === activeTab) {
      window.location.hash = tab;
      return;
    }

    setVisible(false);

    setTimeout(() => {
      setActiveTab(tab);
      window.location.hash = tab;
      setVisible(true);
      setLightbox(null);
      document.body.classList.remove("lightbox-open");
    }, 180);
  };

  const openLightbox = (i) => {
    setLightbox(i);
    document.body.classList.add("lightbox-open");
  };

  const closeLightbox = () => {
    setLightbox(null);
    document.body.classList.remove("lightbox-open");
  };

  useEffect(() => {
    const onHashChange = () => {
      const nextTab = getTabFromHash();
      setActiveTab(nextTab);
      setVisible(true);
      setLightbox(null);
      document.body.classList.remove("lightbox-open");
    };

    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (lightbox === null || activeTab === "suggest") return;

    const handler = (e) => {
      if (e.key === "ArrowRight") {
        setLightbox((i) => Math.min(i + 1, currentItems.length - 1));
      }
      if (e.key === "ArrowLeft") {
        setLightbox((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Escape") {
        closeLightbox();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, currentItems.length, activeTab]);

  useEffect(() => {
    const block = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["s", "u", "p"].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", block);
    return () => window.removeEventListener("keydown", block);
  }, []);

  useEffect(() => {
    return () => document.body.classList.remove("lightbox-open");
  }, []);

  return (
    <Page>
      <Header>
        <HeaderInner>
          <HeaderMeta>Adam Potočnik</HeaderMeta>

          <HeaderActions>
            <SocialRow>
              <SocialLink
                href="https://www.tiktok.com/@adampotocnik"
                target="_blank"
                rel="noreferrer"
              >
                <TikTokIcon /> TikTok
              </SocialLink>

              <SocialLink
                href="https://www.instagram.com/adampotocnikk"
                target="_blank"
                rel="noreferrer"
              >
                <InstagramIcon /> Instagram
              </SocialLink>

              <SocialLink
                href="https://www.youtube.com/@adampotocnik"
                target="_blank"
                rel="noreferrer"
              >
                <YouTubeIcon /> YouTube
              </SocialLink>
            </SocialRow>

            <ContactButton
              type="button"
              $active={activeTab === "contact"}
              aria-current={activeTab === "contact" ? "page" : undefined}
              onClick={() => switchTab("contact")}
            >
              Contact
            </ContactButton>
          </HeaderActions>
        </HeaderInner>
      </Header>

      {activeTab === "contact" ? (
        <ContactPage />
      ) : (
        <>
          <Hero>
            <HeroName>
              Adam
              <br />
              Potočnik
            </HeroName>
            <HeroBio>
              Graphic designer mixing Pop Culture and Design
              <br />
              to create Bold concepts.
            </HeroBio>
          </Hero>

          <TabBar>
            <TabInner>
              {["sneakers", "clothing"].map((tab) => (
                <Tab
                  key={tab}
                  $active={activeTab === tab}
                  onClick={() => switchTab(tab)}
                >
                  {tab === "suggest"
                    ? "Suggest This Week"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </Tab>
              ))}
            </TabInner>
          </TabBar>

          {activeTab === "suggest" ? (
            <SuggestionsTab />
          ) : (
            <GridArea
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(8px)",
                transition: "opacity 0.18s ease, transform 0.18s ease",
              }}
            >
              {currentItems.map((s, i) => (
                <Card
                  key={s.src + i}
                  onClick={() => openLightbox(i)}
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <CardImage>
                    <img src={s.src} alt={s.name} loading="lazy" />

                    {(s.tiktok || s.instagram) && (
                      <CardBadges>
                        {s.tiktok && (
                          <BadgeLink
                            href={s.tiktok}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="TikTok"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <TikTokIcon />
                          </BadgeLink>
                        )}

                        {s.instagram && (
                          <BadgeLink
                            href={s.instagram}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Instagram"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <InstagramIcon />
                          </BadgeLink>
                        )}
                      </CardBadges>
                    )}
                  </CardImage>

                  <CardBody>
                    <CardName>{s.name}</CardName>
                  </CardBody>
                </Card>
              ))}
            </GridArea>
          )}
        </>
      )}

      {lightbox !== null && activeTab !== "suggest" && currentItems[lightbox] && (
        <Overlay onClick={closeLightbox}>
          <LightboxInner
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => {
              touchStart.current = e.touches[0].clientX;
            }}
            onTouchEnd={(e) => {
              if (touchStart.current === null) return;

              const diff = touchStart.current - e.changedTouches[0].clientX;

              if (diff > 50) {
                setLightbox((i) => Math.min(i + 1, currentItems.length - 1));
              }

              if (diff < -50) {
                setLightbox((i) => Math.max(i - 1, 0));
              }

              touchStart.current = null;
            }}
          >
            <CloseBtn onClick={closeLightbox} aria-label="Close">
              ✕
            </CloseBtn>

            <LightboxImageWrap>
              <img
                src={currentItems[lightbox].src}
                alt={currentItems[lightbox].name}
                onContextMenu={(e) => e.preventDefault()}
              />
            </LightboxImageWrap>

            <LightboxFooter>
              <LightboxMeta>
                <LightboxName>{currentItems[lightbox].name}</LightboxName>

                {(currentItems[lightbox].tiktok || currentItems[lightbox].instagram) && (
                  <LightboxLinks>
                    {currentItems[lightbox].tiktok && (
                      <LightboxLink
                        href={currentItems[lightbox].tiktok}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <TikTokIcon />
                        TikTok
                      </LightboxLink>
                    )}

                    {currentItems[lightbox].instagram && (
                      <LightboxLink
                        href={currentItems[lightbox].instagram}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <InstagramIcon />
                        Instagram
                      </LightboxLink>
                    )}
                  </LightboxLinks>
                )}
              </LightboxMeta>

              <LightboxNav>
                <NavBtn
                  onClick={() => setLightbox((i) => Math.max(i - 1, 0))}
                  disabled={lightbox === 0}
                  aria-label="Previous"
                >
                  ←
                </NavBtn>

                <NavBtn
                  onClick={() =>
                    setLightbox((i) => Math.min(i + 1, currentItems.length - 1))
                  }
                  disabled={lightbox === currentItems.length - 1}
                  aria-label="Next"
                >
                  →
                </NavBtn>
              </LightboxNav>
            </LightboxFooter>
          </LightboxInner>
        </Overlay>
      )}

      <Footer>
        <FooterText>© {new Date().getFullYear()} Adam Potočnik</FooterText>

        <FooterLinks>
          <FooterLink
            href="https://www.tiktok.com/@adampotocnik"
            target="_blank"
            rel="noreferrer"
          >
            TikTok
          </FooterLink>

          <FooterLink
            href="https://www.instagram.com/adampotocnikk"
            target="_blank"
            rel="noreferrer"
          >
            Instagram
          </FooterLink>

          <FooterLink
            href="https://www.youtube.com/@adampotocnik"
            target="_blank"
            rel="noreferrer"
          >
            YouTube
          </FooterLink>
        </FooterLinks>
      </Footer>
    </Page>
  );
}

/* ---------- animations ---------- */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/* ---------- layout ---------- */
const Page = styled.div`
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(247, 246, 243, 0.9);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--faint);
`;

const HeaderInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.04em;
  color: var(--muted);
  text-transform: uppercase;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const SocialRow = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 660px) {
    display: none;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink);
  text-decoration: none;
  letter-spacing: 0.02em;
  opacity: 0.6;
  transition: opacity 0.15s;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    opacity: 1;
  }
`;

const ContactButton = styled.button`
  padding: 9px 15px;
  border: 1px solid ${({ $active }) => ($active ? "var(--ink)" : "var(--faint)")};
  background: ${({ $active }) => ($active ? "var(--ink)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--surface)" : "var(--ink)")};
  font-family: "DM Sans", sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;

  &:hover {
    background: var(--ink);
    border-color: var(--ink);
    color: var(--surface);
  }
`;

const Hero = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: clamp(48px, 8vw, 96px) 20px clamp(32px, 5vw, 56px);
  animation: ${fadeUp} 0.6s ease both;
`;

const HeroName = styled.h1`
  font-family: "Instrument Serif", Georgia, serif;
  font-weight: 400;
  font-size: clamp(52px, 11vw, 120px);
  line-height: 0.92;
  letter-spacing: -0.02em;
  color: var(--ink);
  margin-bottom: clamp(20px, 3vw, 32px);
`;

const HeroBio = styled.p`
  font-size: clamp(15px, 2vw, 18px);
  font-weight: 300;
  line-height: 1.6;
  color: var(--muted);
  max-width: 480px;
`;

const TabBar = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--faint);
  animation: ${fadeUp} 0.6s 0.1s ease both;
`;

const TabInner = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  position: relative;
  padding: 7px 0;
  font-family: "DM Sans", sans-serif;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? "500" : "400")};
  color: ${({ $active }) => ($active ? "var(--ink)" : "var(--muted)")};
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.15s ease;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    height: 1.5px;
    width: ${({ $active }) => ($active ? "100%" : "0%")};
    background: var(--ink);
    transition: width 0.25s ease;
    transform-origin: left;
  }

  &:hover {
    color: var(--ink);
  }
`;

const GridArea = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 32px 20px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
`;

const Card = styled.article`
  background: var(--surface);
  border: 1px solid var(--faint);
  overflow: hidden;
  cursor: pointer;
  animation: ${fadeUp} 0.5s ease both;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
    border-color: #d4d2ce;
  }
`;

const CardImage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #f0ede8;
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s ease;
    pointer-events: none;
    -webkit-user-drag: none;
    user-drag: none;
  }

  ${Card}:hover & img {
    transform: scale(1.04);
  }
`;

const CardBadges = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 2;
  display: flex;
  gap: 6px;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.2s ease, transform 0.2s ease;

  ${Card}:hover & {
    opacity: 1;
    transform: translateY(0);
  }
`;

const BadgeLink = styled.a`
  position: relative;
  z-index: 3;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  background: rgba(247, 246, 243, 0.92);
  color: var(--ink);
  border: 1px solid transparent;
  text-decoration: none;
  backdrop-filter: blur(4px);
  transition: border-color 0.15s;

  svg {
    width: 14px;
    height: 14px;
    display: block;
  }

  &:hover {
    border-color: var(--ink);
  }
`;

const CardBody = styled.div`
  padding: 12px 14px 14px;
`;

const CardName = styled.h3`
  font-family: "DM Sans", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: 0.01em;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(247, 246, 243, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const LightboxInner = styled.div`
  position: relative;
  width: min(600px, 100%);
  display: flex;
  flex-direction: column;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: -44px;
  right: 0;
  background: transparent;
  border: 1px solid transparent;
  color: var(--muted);
  font-size: 14px;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: var(--ink);
    border-color: var(--ink);
  }
`;

const LightboxImageWrap = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border: 1px solid var(--faint);
  border-bottom: none;
  background: #f0ede8;
  position: relative;
  user-select: none;
  -webkit-user-select: none;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
  }

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    pointer-events: none;
    -webkit-user-drag: none;
    user-drag: none;
  }
`;

const LightboxFooter = styled.div`
  border: 1px solid var(--faint);
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: var(--surface);
`;

const LightboxMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const LightboxName = styled.h2`
  font-family: "Instrument Serif", serif;
  font-size: clamp(18px, 4vw, 26px);
  font-weight: 400;
  letter-spacing: -0.02em;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LightboxLinks = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const LightboxLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--muted);
  text-decoration: none;
  transition: color 0.15s ease, opacity 0.15s ease;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    color: var(--ink);
    opacity: 1;
  }
`;

const LightboxNav = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const NavBtn = styled.button`
  background: transparent;
  border: 1px solid var(--faint);
  color: var(--ink);
  font-size: 16px;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: border-color 0.15s, opacity 0.15s;
  opacity: ${({ disabled }) => (disabled ? 0.2 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};

  &:hover {
    border-color: var(--ink);
  }
`;

const Footer = styled.footer`
  margin-top: auto;
  border-top: 1px solid var(--faint);
  padding: 24px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
`;

const FooterText = styled.span`
  font-size: 12px;
  color: var(--muted);
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 20px;
`;

const FooterLink = styled.a`
  font-size: 12px;
  color: var(--muted);
  text-decoration: none;

  &:hover {
    color: var(--ink);
  }
`;

function TikTokIcon() {
  return (
    <svg viewBox="0 0 256 256" aria-hidden fill="currentColor">
      <path d="M168.1 48.3c8.5 12.3 21.7 21.3 37.6 24.6v39.4c-14.3-1.5-27.7-6.2-39.6-13.5v52.1c0 38.4-31.1 69.6-69.6 69.6s-69.6-31.2-69.6-69.6 31.2-69.6 69.6-69.6c3.9 0 7.7.3 11.4 1v41.3a28.4 28.4 0 0 0-11.4-2.3 28.3 28.3 0 1 0 28.3 28.3V16h42.7v32.3z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg viewBox="0 0 256 256" aria-hidden fill="currentColor">
      <path d="M128 72a56 56 0 1 0 0 112 56 56 0 0 0 0-112Zm0 92a36 36 0 1 1 0-72 36 36 0 0 1 0 72Zm68-96a12 12 0 1 1 0-24 12 12 0 0 1 0 24Zm40-4v120c0 30.9-25.1 56-56 56H76c-30.9 0-56-25.1-56-56V64C20 33.1 45.1 8 76 8h104c30.9 0 56 25.1 56 56Zm-20 0c0-19.9-16.1-36-36-36H76c-19.9 0-36 16.1-36 36v120c0 19.9 16.1 36 36 36h104c19.9 0 36-16.1 36-36V64Z" />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 256 256" aria-hidden fill="currentColor">
      <path d="M234.33 69.52a24.34 24.34 0 0 0-17.09-17.2C195.78 48 128 48 128 48s-67.78 0-89.24 4.32a24.34 24.34 0 0 0-17.09 17.2C17 91 17 128 17 128s0 37 4.67 58.48a24.34 24.34 0 0 0 17.09 17.2c21.46 4.32 89.24 4.32 89.24 4.32s67.78 0 89.24-4.32a24.34 24.34 0 0 0 17.09-17.2c4.67-21.48 4.67-58.48 4.67-58.48s0-37-4.67-58.48ZM104 162V94l64 34-64 34Z" />
    </svg>
  );
}